import React, { useState, useMemo, useEffect, useRef, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { PatientDataContext } from '../../../context/PatientDataContext';
import './styles/ReviewDocuments.css';
import { db, auth } from '../../../firebase';
import { TriggerRenderDocTemplate } from '../../../utils/TriggerRenderDocTemplate';
import { InitialWarningDialog } from './components/InitialWarningDialog';
import { doc, updateDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import WarningBar from './components/WarningBar';
import DocumentReviewHeader from './components/DocumentReviewHeader';
import { decodeHtmlEntities } from './functions/htmlEncoders';
import { handlePlainTextPaste, suppressFormattingShortcuts } from './functions/contentEditableUtils';
import { isPatientFemale } from '../../../utils/conditionChecks';
import { rerenderPatientReportIfStale } from '../../../utils/autoPatientReport';
import { rerenderCoverPageIfStale } from '../../../utils/autoCoverPage';
import { renderModelInput } from '../../../utils/renderModelInput';
import { useApprovalTracking } from '../../../hooks/useApprovalTracking';
import { usePendingApprovals } from '../../../hooks/usePendingApprovals';
import { finalizeSectionReview } from '../../../utils/sectionReviewFinalization';
import { computeUnpersistedApprovals } from '../../../utils/approvalStateUtils';
import { toast } from 'react-toastify';
import { useFloatingActionPanel } from './components/floatingPanel/useFloatingActionPanel';
import { FloatingActionPanel } from './components/floatingPanel/FloatingActionPanel';
import { buildCIExclusionsContext } from '../../../utils/clinicalInterviewWorkflow';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { ASSESSMENT_CONCLUSION_SECTION_TITLE } from '../../../utils/assessmentEnd';
import { appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { TriggerSembleMdtReport } from '../../../utils/TriggerSembleMdtReport';
import { getDirtySembleCiParagraphs } from '../../../utils/sembleCiReportStaleness';
import { TriggerSSICompletionEmail } from './functions/TriggerSSICompletionEmail';
import {
  emitParagraphApproval,
  emitParagraphEdited,
  emitParagraphReset,
} from '../../../analytics/reportEvents';
import { useAnthropicComparison } from '../../../hooks/useAnthropicComparison';
import { CompareModeToolbar } from './components/CompareModeToolbar';
import { CompareClaudePanel } from './components/CompareClaudePanel';

// NOTE: the Semble MDT Report's Clinical Interview Summary and Risk Assessment
// paragraphs are summarised from APPROVED CI report paragraphs, so editing &
// re-approving those on this page can leave the Semble summaries stale.  The
// source lists + dirty-check live in utils/sembleCiReportStaleness so the
// logic is unit-tested and kept in one place.  The Semble Physical Health
// paragraph is deliberately NOT covered here — it reads raw Medical History
// form inputs and has its own dirty-check in semblePhysicalHealthStaleness.js.

export default function ReviewDocumentCI() {

  const { patientId } = useParams();

  const MODULE_NUMBER = 1
  const MODULE_PREFIX_CODE = "ci"
  const MODULE_TITLE = "Clinical Interview"
  const navigate = useNavigate();
  const { patientDataModel, fetchPatientData, clearPatientData } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const organisationName = claims?.organisation || 'default';

  const wf = useWorkflowConfig(patientDataModel, claims);
  const patientIsFemale = useMemo(() => {
    return isPatientFemale(patientDataModel);
}, [patientDataModel]);

  useEffect(() => {
    // Force fresh fetch
    fetchPatientData(patientId);
  }, [patientId, fetchPatientData]);

  const docStructure = useMemo(() => {
    // Create the base structure
    const baseStructure = {
      "Presenting Complaint": {
        "1": { "title": "Referral Information", "code": "ci_COM_referral_information" },
        "2": { "title": "History of Presenting Complaint", "code": "ci_COM_history_of_presenting_complaint" },
        "3": { "title": "Impact on Daily Functioning", "code": "ci_COM_impact_on_daily_functioning" },
      },
      "Developmental History": {
        "1": { "title": "Pre-natal and Birth", "code": "ci_DEV_pre_natal_and_birth" },
        "2": { "title": "Early Years", "code": "ci_DEV_early_years" },
        "3": { "title": "Childhood and Adolescence", "code": "ci_DEV_childhood_and_adolescence" },
        "4": { "title": "Childhood Living Environment", "code": "ci_DEV_childhood_living_environment" },
        "5": { "title": "Education", "code": "ci_DEV_education" },
      },
      "Personal and Social History": {
        "1": { "title": "Current Living Situation", "code": "ci_DEV_current_living_situation" },
        "2": { "title": "Relationships", "code": "ci_DEV_relationships" },
        "3": { "title": "Employment", "code": "ci_DEV_employment" },
      },
      "Lifestyle History": {
        "1": { "title": "Lifestyle Factors", "code": "ci_LIF_lifestyle_factors" },
        "2": { "title": "Alcohol and Substance Use", "code": "ci_LIF_alcohol_and_substance_use" },
        "3": { "title": "Habits", "code": "ci_LIF_habits" },
        "4": { "title": "Driving", "code": "ci_LIF_driving" },
      },
      "Forensic History": {
        "1": { "title": "History of Legal Issues", "code": "ci_FOR_history_of_legal_issues" },
        "2": { "title": "History as a Perpetrator", "code": "ci_FOR_perpetrator_history" },
        "3": { "title": "History as a Victim", "code": "ci_FOR_victim_history" },
      },
      "Family History": {
        "1": { "title": "Family Health Conditions", "code": "ci_FAM_health_conditions" },
      },
      "Medical History": {
        "1": { "title": "Health Conditions", "code": "ci_MED_health_conditions" },
        "2": { "title": "Hospital Visits", "code": "ci_MED_hospital_visits" },
        "3": { "title": "Medications", "code": "ci_MED_medications" },
        "4": { "title": "Allergies", "code": "ci_MED_allergies" },
        "5": { "title": "Vision and Hearing", "code": "ci_MED_vision_and_hearing" },
      },
      "Psychiatric History": {
        "1": { "title": "Previous and Current Diagnoses", "code": "ci_PSY_previous_and_current_diagnoses" },
        "2": { "title": "Previous and Current Treatments", "code": "ci_PSY_previous_and_current_treatments" },
        "3": { "title": "Trauma and Risk History", "code": "ci_PSY_trauma_and_risk_history" },
      },
      "Mental State Examination": {
        "1": { "title": "Summary", "code": "ci_MSE_summary" },
      },
      "Risk Assessment": {
        "1": { "title": "Summary", "code": "ci_RSK_summary" },
      },
      // Conditional-CAR workflows (TOC, SLaM, WLC) do not generate or display
      // ci_DF — their CI CAR/Patient Report render formulation from
      // dd_finalFormulation, not this CI paragraph, and the backend suppresses
      // its generation for them (see workflow_config_utils.get_suppressed_generation_codes).
      // For standard workflows it remains a reviewable section.
      ...(wf.usesConditionalCarPipeline ? {} : {
        [wf.isFlatDifferentials
          ? "Formulation and Conditions Under Consideration"
          : "Formulation and Provisional Diagnosis"]: {
          "1": { "title": "Summary", "code": "ci_DF" },
        },
      }),
      // Assessment Conclusion (one-clinician SLaM / 3 Borough only). When the
      // Lead chose "End assessment" at the end of the Clinical Interview the
      // CI report closes with a paragraph recording that decision and the
      // reason they gave (ci_END_summary). It is a stage-level pseudo-section
      // with no numbered CI page, so it lives only here and in the backend's
      // GenerateDraftWithLLM routing (keep the title string identical).
      //
      // Shown ONLY while the assessment is ended. Resuming the assessment
      // drops it from the structure, which is all the cleanup needed: approval
      // is derived from docStructure x outputs, so a stale ci_END_summary
      // output can neither appear in the review nor block approval, and the
      // backend suppresses regenerating it until the assessment is ended again.
      ...(wf.assessmentEndedAfterCI ? {
        [ASSESSMENT_CONCLUSION_SECTION_TITLE]: {
          "1": { "title": "Summary", "code": "ci_END_summary" },
        },
      } : {}),
    }

    // Add Women's Health section if patient is female
    if (patientIsFemale) {
      // Add a new entry to the Medical History section (making sure to reindex properly)
      baseStructure["Medical History"]["6"] = { "title": "Women's Health", "code": "ci_MED_womens_health" };
    }

    // Remove MSE section entirely if the organisation/patient excludes it in CI
    const { excludedTitles } = buildCIExclusionsContext(patientDataModel, organisationName);
    if (excludedTitles && excludedTitles.includes('Mental Status Examination')) {
      delete baseStructure["Mental State Examination"];
    }

    return baseStructure;
  }, [patientIsFemale, patientDataModel, organisationName, wf.isFlatDifferentials, wf.usesConditionalCarPipeline, wf.assessmentEndedAfterCI]);


  const REPORT_DATA_OUTPUTS = useMemo(() =>
    patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.outputs || {},
    [patientDataModel]);

  // The paragraph edit history, read from the SAME already-loaded section as the
  // outputs above. It is what lets the adoption numbers (how much of the AI draft
  // survived) be computed at the moment of an edit rather than by reading the
  // report back out of Firestore later - and because this object is already in
  // memory to render the page, it costs no additional read.
  //
  // In-memory means it can lag a write made elsewhere in the same instant. That
  // only matters for a paragraph's FIRST edit, and only if another session edited
  // it seconds earlier; once the earliest entry exists it stays the earliest
  // forever. The nightly reconciliation reads authoritative Firestore, so a
  // disagreement surfaces there rather than going unnoticed.
  const REPORT_EDIT_HISTORY = useMemo(() =>
    patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.editHistory || {},
    [patientDataModel]);

  const [state, setState] = useState({
    selectedCode: null,
    textareaContent: null,
    isSidePanelOpen: false,
    modelInputsFirestore: null,
    clinicianInputs: null,
    clinicianDecisions: null,
    patientInputs: null,
    informantInputs: null
  });

  const [isEditing, setIsEditing] = useState(false);

  const {
    totalParagraphs, reviewedCodes, isFullyApproved,
    showWarning, addReviewedCode, removeReviewedCode,
  } = useApprovalTracking({
    patientId,
    moduleNumber: MODULE_NUMBER,
    docStructure,
    reportDataOutputs: REPORT_DATA_OUTPUTS,
  });

  // Tracks in-flight per-paragraph approval writes so Save & Exit can wait for
  // them to commit before deriving and persisting the section approval flag.
  const { track: trackApproval, settleAll: settlePendingApprovals } = usePendingApprovals();

  // ── Dual-model comparison (dev/staging, fallback-enabled orgs only) ─────
  // CI Firestore section titles match these docStructure keys, so they can
  // be sent as reportSection to GenerateDraftWithLLM.
  const ciSections = useMemo(() => Object.keys(docStructure), [docStructure]);
  const {
    isAvailable: isCompareAvailable,
    isLoading: isCompareLoading,
    hasComparison,
    showClaude,
    sideBySide,
    claudeOutputs,
    progress: compareProgress,
    triggerComparison,
    setViewMode,
    toggleSideBySide,
    getDisplayedOutput,
  } = useAnthropicComparison({
    patientId,
    reportStage: MODULE_TITLE,
    reportSections: ciSections,
    conditions: [],
    orgId: patientDataModel?.orgId || claims?.orgId,
    organisationName: claims?.organisation,
  });

  const handleParagraphClick = (event, code, content) => {
    // Reset edit states
    setIsEditing(false);

    if (!state.modelInputsFirestore) {
      console.error('modelInputsFirestore is not populated');
      return;
    }

    if (code && state.modelInputsFirestore[code]) {
      setState(prevState => ({
        ...prevState,
        selectedCode: code,
        textareaContent: content || ' ', // always having at least a non-breaking space
        clinicianInputs: state.modelInputsFirestore[code]["Clinician Notes"],
        clinicianDecisions: state.modelInputsFirestore[code]["Clinician Decisions"],
        patientInputs: state.modelInputsFirestore[code]["Patient Prefill"],
        informantInputs: state.modelInputsFirestore[code]["Informant Prefill"],
        isSidePanelOpen: true
      }));
      // Position the panel relative to this paragraph
      setTimeout(() => {
        safeUpdatePosition(code);
      }, 0);
    } else {
      console.error('No matching input found for the code:', code);
    }
  };

  // Update the Section component to only render paragraphs with content
  const Section = ({ title, paragraphs }) => {
    // First filter out paragraphs that don't have content in REPORT_DATA_OUTPUTS
    const availableParagraphs = Object.entries(paragraphs).filter(
      ([_, { code }]) => REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== ""
    );

    // Only render the section if it has at least one available paragraph
    if (availableParagraphs.length === 0) return null;

    return (
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4 text-blue-500">{title}</h2>

        {availableParagraphs.map(([key, { title: subTitle, code }]) => {
          const content = getDisplayedOutput(code, REPORT_DATA_OUTPUTS[code]);
          const decodedContent = decodeHtmlEntities(content); // Decode for display        
          return (
            <div key={key} className="mb-4">
              {subTitle && (
                <h3 className="font-semibold mb-2 text-gray-900">
                  {subTitle}
                </h3>
              )}
              <div
                data-paragraph-container
                className={`
                relative text-sm font-medium p-2 rounded-lg
                transition-all duration-200
                ${isEditing && state.selectedCode === code
                    ? 'bg-amber-100 z-20' // Edit mode background
                    : reviewedCodes.has(code)
                      ? 'bg-green-600/30 hover:bg-green-600/40 approved-paragraph'
                      : 'bg-violet-100 hover:bg-violet-200'
                  }
                ${state.selectedCode === code
                    ? 'ring ring-violet-800'
                    : ''
                  }
                ${!isEditing ? 'cursor-pointer' : ''}
              `}
                id={code}
                onClick={(e) => {
                  if (!isEditing) {
                    handleParagraphClick(e, code, decodedContent);
                  }
                }}
              >
                <p
                  ref={editableContentRef}
                  className="text-gray-700 leading-relaxed whitespace-pre-wrap"
                  contentEditable={isEditing && state.selectedCode === code && !showClaude}
                  onKeyDown={suppressFormattingShortcuts}
                  onInput={(e) => {
                    if (isEditing && state.selectedCode === code) {
                      let newContent = e.target.innerText;
                      newContent = newContent.replace(/\n/g, '\n');

                      if (REPORT_DATA_OUTPUTS) {
                        REPORT_DATA_OUTPUTS[code] = newContent;
                      }
                      safeUpdatePosition(code);
                    }
                  }}
                  onPaste={handlePlainTextPaste}
                  suppressContentEditableWarning={true}
                >
                  {decodedContent}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Add a ref for the paragraph content
  const editableContentRef = useRef(null);

  // Document container
  // Update handleStartEdit to focus the content
  const handleStartEdit = () => {
    if (state.selectedCode) {
      // Store the current content before starting edit
      setPreEditContent(prev => ({
        ...prev,
        [state.selectedCode]: REPORT_DATA_OUTPUTS[state.selectedCode] || ''
      }));
    }
    setIsEditing(true);
    // Use setTimeout to ensure the contentEditable is enabled before focusing
    setTimeout(() => {
      const paragraphElement = document.querySelector(`#${state.selectedCode} p`);
      if (paragraphElement) {
        paragraphElement.focus();
        // Create a range and selection to place cursor at the end
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(paragraphElement);
        range.collapse(false); // false means collapse to end
        selection.removeAllRanges();
        selection.addRange(range);
      }
      // Snap panel position right after entering edit mode
      safeUpdatePosition(state.selectedCode);
    }, 0);
  };

  const handleSaveEdit = () => {
    if (state.selectedCode) {
      // Get the current and previous values
      const spanCode = state.selectedCode;
      const newValue = REPORT_DATA_OUTPUTS[spanCode];
      const previousValue = initialContent[spanCode] || '';

      // Only proceed if content actually changed
      if (newValue !== previousValue) {
        // Optimistically update UI first
        removeReviewedCode(state.selectedCode);
        setIsEditing(false);
        // Recompute position after leaving edit mode
        safeUpdatePosition(state.selectedCode);

        // Create history entry
        const editEntry = {
          clinicianId: auth.currentUser.uid,
          clinicianName: auth.currentUser.displayName,
          timestamp: serverTimestamp(),
          previousValue,
          newValue,
          editType: "manual",
        };
        const timestampKey = Date.now().toString();

        // Base path to this section. MODULE_NUMBER and timestampKey are numeric
        // segments; Firestore's SDK backtick-escapes digit-leading segments
        // automatically when it builds the update mask, so the dotted-string key
        // form is safe here (and matches handleParagraphApproval exactly).
        const sectionPath = `dataModel.6_manageReports.sections.${MODULE_NUMBER}`;

        // Write ONLY the affected keys with a plain updateDoc rather than a
        // read-modify-write runTransaction. Transactions commit with a
        // document-version precondition; on slow 3G/4G the single patient doc is
        // being touched concurrently (in-flight approvals, auto report/cover
        // re-renders, lastReviewedAt bumps) so the transaction exhausts its
        // retries and throws "the stored version does not match the required
        // base version" — which silently discarded the clinician's edit. A
        // targeted updateDoc has no version precondition and Firestore merges
        // writes to distinct field paths server-side. See handleParagraphApproval
        // for the same reasoning applied to approvals.
        const updateData = {
          // Persist the edited text (targeted, replacing the whole-map rewrite)
          [`${sectionPath}.outputs.${spanCode}`]: newValue,
          // Un-approve this paragraph: editing invalidates a prior approval
          [`${sectionPath}.approvedParagraphs.${spanCode}`]: deleteField(),
          // Append an audit entry under this paragraph's edit history
          [`${sectionPath}.editHistory.${spanCode}.${timestampKey}`]: editEntry,
          // Review metadata (mirrors the approval write)
          [`${sectionPath}.lastReviewedAt`]: serverTimestamp(),
          [`${sectionPath}.reviewedBy`]: auth.currentUser.uid,
          [`${sectionPath}.reviewedByName`]: auth.currentUser.displayName,
        };

        // Perform Firestore update in the background
        const startedAt = performance.now();
        updateDoc(doc(db, 'patients', patientId), updateData).then(() => {
          // Confirms the edit persisted AND the paragraph was un-approved
          // (removed from approvedParagraphs) in Firestore. Code-only, no PII.
          const elapsedMs = Math.round(performance.now() - startedAt);
          console.log(`[edit] saved & UNAPPROVED "${spanCode}" (content updated) in ${elapsedMs}ms`);
          // Analytics mirror of the edit that just persisted: paragraph code,
          // section and the two lengths only — never the wording itself.
          emitParagraphEdited({
            patientId,
            module: MODULE_PREFIX_CODE,
            docStructure,
            code: spanCode,
            prevText: previousValue,
            priorHistory: REPORT_EDIT_HISTORY[spanCode] || {},
            nextText: newValue,
          });
        }).catch(error => {
          console.error("Error updating paragraph approval on edit:", error);
          // Roll back optimistic update on error and surface the failure
          addReviewedCode(state.selectedCode);
          toast.error(
            'Your edit did not save. Please try editing the paragraph again.',
            { toastId: 'paragraph-edit-save-error' }
          );
        });
        // Mark section as dirty since a change was saved
        setSectionDirty(true);
      } else {
        // If no changes, just exit edit mode
        setIsEditing(false);
        safeUpdatePosition(state.selectedCode);
      }
    }
  };

  // Update handleCancel to revert to pre-edit content
  const handleCancel = () => {
    if (state.selectedCode && preEditContent[state.selectedCode]) {
      // Revert to the content before this edit session started
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[state.selectedCode] = preEditContent[state.selectedCode];
      }
    }
    setIsEditing(false);
    safeUpdatePosition(state.selectedCode);
  };

  const handleReset = () => {
    if (state.selectedCode && initialContent[state.selectedCode]) {
      const spanCode = state.selectedCode;
      const newValue = initialContent[state.selectedCode];
      const previousValue = REPORT_DATA_OUTPUTS[state.selectedCode];

      // Optimistically update UI
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[state.selectedCode] = initialContent[state.selectedCode];
      }

      // Remove from reviewed codes optimistically
      removeReviewedCode(state.selectedCode);

      // Create history entry for the reset
      const editEntry = {
        clinicianId: auth.currentUser.uid,
        clinicianName: auth.currentUser.displayName,
        timestamp: serverTimestamp(),
        previousValue,
        newValue,
        isReset: true  // Flag to indicate this was a reset action
      };
      const timestampKey = Date.now().toString();

      // Same targeted-write approach as handleSaveEdit / handleParagraphApproval:
      // a plain updateDoc on specific field paths (no version precondition)
      // instead of a read-modify-write runTransaction, which contends on the
      // single patient doc and fails under slow 3G/4G (losing the reset).
      const sectionPath = `dataModel.6_manageReports.sections.${MODULE_NUMBER}`;
      const updateData = {
        // Restore the original generated text
        [`${sectionPath}.outputs.${spanCode}`]: newValue,
        // A reset also invalidates any prior approval
        [`${sectionPath}.approvedParagraphs.${spanCode}`]: deleteField(),
        // Record the reset in this paragraph's edit history
        [`${sectionPath}.editHistory.${spanCode}.${timestampKey}`]: editEntry,
        // Review metadata (mirrors the approval write)
        [`${sectionPath}.lastReviewedAt`]: serverTimestamp(),
        [`${sectionPath}.reviewedBy`]: auth.currentUser.uid,
        [`${sectionPath}.reviewedByName`]: auth.currentUser.displayName,
      };

      // Perform Firestore update
      const startedAt = performance.now();
      updateDoc(doc(db, 'patients', patientId), updateData).then(() => {
        // Confirms the reset persisted AND the paragraph was un-approved
        // (removed from approvedParagraphs) in Firestore. Code-only, no PII.
        const elapsedMs = Math.round(performance.now() - startedAt);
        console.log(`[reset] reset & UNAPPROVED "${spanCode}" in ${elapsedMs}ms`);
        // Analytics mirror of the reset that just persisted (code only).
        emitParagraphReset({
          patientId,
          module: MODULE_PREFIX_CODE,
          docStructure,
          code: spanCode,
        });
      }).catch(error => {
        console.error("Error resetting paragraph:", error);
        // Roll back optimistic updates on error and surface the failure
        if (REPORT_DATA_OUTPUTS) {
          REPORT_DATA_OUTPUTS[state.selectedCode] = previousValue;
        }
        addReviewedCode(state.selectedCode);
        toast.error(
          'The paragraph reset did not save. Please try again.',
          { toastId: 'paragraph-reset-save-error' }
        );
      });

      setIsEditing(false);
      // Mark section as dirty since content was reset
      setSectionDirty(true);
      safeUpdatePosition(state.selectedCode);
    }
  };

  // Helper function to check if content has been modified from original
  const hasBeenModified = (code) => {
    if (!code) return false;
    return REPORT_DATA_OUTPUTS[code] !== initialContent[code];
  };

  const [showInitialWarning, setShowInitialWarning] = useState(true);
  // showWarning is provided by useApprovalTracking

  // State for initial content (on page load)
  const [initialContent, setInitialContent] = useState({});

  // Store initial content on page load, only for paragraphs with content
  useEffect(() => {
    if (REPORT_DATA_OUTPUTS) {
      const initialValues = {};
      // Loop through docStructure to get all codes
      Object.values(docStructure).forEach(section => {
        Object.values(section).forEach(({ code }) => {
          // Only store content that exists
          if (code && REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== "") {
            initialValues[code] = REPORT_DATA_OUTPUTS[code];
          }
        });
      });
      setInitialContent(initialValues);
    }
  }, [REPORT_DATA_OUTPUTS, docStructure]);

  // State for pre-edit content (before current edit session)
  const [preEditContent, setPreEditContent] = useState({});

  const documentContainerRef = useRef(null);

  // Use shared floating panel hook
  const { panelRef, panelPosition, safeUpdatePosition } = useFloatingActionPanel({
    containerRef: documentContainerRef,
    selectedCode: state.selectedCode,
    isEditing,
    topOffset: -15,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [sectionDirty, setSectionDirty] = useState(false);

  const handleExitReview = async () => {
    setIsSaving(true);

    try {
      // Wait for any optimistic per-paragraph approval writes to commit so the
      // finalisation below reads the true, current approval state from Firestore.
      await settlePendingApprovals();

      // Derive and persist the section approval flag transactionally from the
      // fresh document snapshot (see sectionReviewFinalization). This replaces
      // the previous pattern of writing the client's optimistic isFullyApproved,
      // which could diverge from Firestore and finalise an incomplete report.
      const { isFullyApproved: approvedNow, outstandingCodes, manageReports } = await finalizeSectionReview({
        patientId,
        modulePrefix: MODULE_PREFIX_CODE,
        moduleNumber: MODULE_NUMBER,
        docStructure,
      });

      // Reconcile the optimistic UI against the authoritative snapshot before
      // allowing the exit. Stay on the page when either:
      //   1. "Phantom approvals" exist — paragraphs shown green whose background
      //      write actually failed (rapid clicking can overload the per-document
      //      transaction queue), or
      //   2. the UI believed everything was approved but the snapshot found
      //      outstanding content (e.g. paragraphs generated after page load).
      // The affected paragraphs are reverted to needs-review and the patient
      // document is re-fetched so the page reflects reality.
      const unpersisted = computeUnpersistedApprovals(reviewedCodes, outstandingCodes);
      if (!approvedNow && (isFullyApproved || unpersisted.length > 0)) {
        unpersisted.forEach(removeReviewedCode);
        await fetchPatientData(patientId);
        toast.error(
          unpersisted.length > 0
            ? `${unpersisted.length} paragraph approval${unpersisted.length === 1 ? ' did' : 's did'} not save. `
              + 'The affected paragraphs have been returned to "needs review" — please approve them again.'
            : 'Some paragraphs still need review before this section can be completed. '
              + 'The page has been refreshed to show them.',
          { toastId: 'review-exit-unsaved' }
        );
        return;
      }

      // Only render / trigger downstream when the AUTHORITATIVE (snapshot-derived)
      // approval is true AND this section was actually changed this session.
      const shouldRenderSection = approvedNow && sectionDirty;

      appendReportAuditEntry(patientId, {
        module: MODULE_PREFIX_CODE,
        eventType: 'approved',
        isFirstOccurrence: approvedNow && !manageReports?.ciReportFirstApprovedAt,
      });

      // Only trigger document generation if fully approved AND this section was changed during this session
      if (shouldRenderSection) {
        TriggerRenderDocTemplate({
          reportStage: "Clinical Interview",
          clinicianId: auth.currentUser.uid,
          patientId: patientId
        });
      }

      // MDT-information-available email. The server decides when it is ready
      // per org (see TriggerSSICompletionEmail.js): single-clinician orgs
      // (TOC, WLC) wait for BOTH reports, so firing from the CI exit as well
      // as the SSI exit (RD_SSI.js) makes the email order-independent;
      // two-clinician orgs (Phoenix, SLaM) send on SSI approval alone, so
      // this call is a no-op for them unless the SSI was already approved
      // and the email has not yet gone out. The server skips without
      // stamping when not ready and deduplicates by stamp once sent. Gated on
      // the snapshot-derived approval so an unapproved exit does not spend a
      // callable invocation that would only be skipped.
      if (approvedNow) {
        TriggerSSICompletionEmail(patientId, auth.currentUser.uid);
      }

      // Kick off a background check to re-render the Patient Report if it's stale.
      // Fire-and-forget to keep the UI snappy; the helper is idempotent and skips if not needed.
      rerenderPatientReportIfStale({
        patientId,
        clinicianId: auth.currentUser.uid,
        patientDataModel,
        organisation: organisationName
      }).catch((e) => console.error('rerenderPatientReportIfStale (CI) failed:', e));

      // Also re-render the Cover Page if stale (same gating as Patient Report, per flow)
      rerenderCoverPageIfStale({
        patientId,
        clinicianId: auth.currentUser.uid,
        patientDataModel,
        organisation: organisationName
      }).catch((e) => console.error('rerenderCoverPageIfStale (CI) failed:', e));

      // Semble MDT Report staleness: if the Semble report was already generated
      // and any CI paragraph that feeds one of its summaries was edited this
      // session, trigger targeted regeneration of ONLY the affected Semble
      // paragraph(s).  This fires on page EXIT (not on each edit/approval) so a
      // clinician can edit/re-approve several CI paragraphs and only regenerate
      // once.  Covered here:
      //   - semble_risk_assessment ← ci_RSK_summary
      //   - semble_clinical_history ← the Presenting-Complaint-to-Psychiatric-
      //     History CI paragraphs (SEMBLE_CLINICAL_HISTORY_SOURCES)
      // Physical Health is regenerated separately when Medical History form
      // inputs or panel notes change (see semblePhysicalHealthStaleness.js /
      // CIPrefilledPage) — it reads raw form data, not these CI paragraphs.
      if (sectionDirty && wf.isSembleMdtReportEnabled && manageReports?.sembleMDTReportGenerated) {
        const dirtyKeys = getDirtySembleCiParagraphs(manageReports, REPORT_DATA_OUTPUTS);
        if (dirtyKeys.length > 0) {
          TriggerSembleMdtReport({
            clinicianId: auth.currentUser.uid,
            patientId,
            forceRegenerateKeys: dirtyKeys,
            waitAndRetry: true,
          });
        }
      }

      // Clear any cached data before navigating
      clearPatientData();
      // Reset dirty flag for next session
      setSectionDirty(false);

      // Navigate back to documents page
      navigate(`/patient/${patientId}/documents`);
    } catch (error) {
      console.error('Error during document review completion:', error);
      // Surface the failure so the clinician knows the exit did not complete —
      // silently staying on the page looks like a frozen button.
      toast.error(
        'Could not save your review just now. Please check your connection and try again.',
        { toastId: 'review-exit-error' }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const goToFirstUnreviewed = () => {
    // Only get paragraphs that are actually rendered on the page
    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const firstUnreviewed = Array.from(allParagraphs).find(p => !reviewedCodes.has(p.id));

    if (firstUnreviewed && documentContainerRef.current) {
      const containerRect = documentContainerRef.current.getBoundingClientRect();
      const nextRect = firstUnreviewed.getBoundingClientRect();

      // Calculate scroll position to center the paragraph
      const scrollTop = documentContainerRef.current.scrollTop +
        (nextRect.top - containerRect.top) -
        containerRect.height / 3;

      // Scroll to position
      documentContainerRef.current.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      });

      // After scroll completes, trigger click handler
      setTimeout(() => {
        const nextCode = firstUnreviewed.id;
        const nextContent = REPORT_DATA_OUTPUTS[nextCode];

        // Trigger the full paragraph click handler
        handleParagraphClick(
          { currentTarget: firstUnreviewed },
          nextCode,
          nextContent
        );
      }, 300);
    }
  };

  useEffect(() => {
    // Apply the overflow: hidden style when the component mounts
    document.body.style.overflow = 'hidden';
    // Clean up the style when the component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Add useEffect to fetch model inputs
  useEffect(() => {
    const fetchModelInputs = async () => {
      try {
        const inputs = patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.inputs;
        if (inputs) {
          setState(prev => ({ ...prev, modelInputsFirestore: inputs }));
        } else {
          console.error('Inputs data is missing');
        }
      } catch (error) {
        console.error('Error fetching model inputs from Firestore:', error);
      }
    };

    if (patientDataModel) {
      fetchModelInputs();
    }
  }, [patientDataModel]);


  // Modify the overlay to explicitly allow scroll events
  useEffect(() => {
    // Store ref in a variable inside the effect
    const currentContainer = documentContainerRef.current;

    if (isEditing && currentContainer) {
      // Save original overflow setting
      const originalOverflow = currentContainer.style.overflow;

      // Ensure the document container is still scrollable during editing
      currentContainer.style.overflow = 'auto';

      // Create a wheel event handler that prevents propagation but allows default behavior
      const handleWheel = (e) => {
        e.stopPropagation();
        // But let the default scroll behavior happen
      };

      // Add the wheel event listener
      currentContainer.addEventListener('wheel', handleWheel, { passive: true });

      // Clean up function uses the stored variable
      return () => {
        currentContainer.removeEventListener('wheel', handleWheel);
        currentContainer.style.overflow = originalOverflow;
      };
    }
  }, [isEditing]);

  const handlePageClick = useCallback((event) => {
    // Ignore clicks if we're in edit mode
    if (isEditing) {
      return;
    }

    // Ignore clicks on paragraphs or the action panel
    if (
      event.target.closest('[data-paragraph-container]') ||
      event.target.closest('[data-action-panel]')
    ) {
      return;
    }
    setIsEditing(false);
    // Reset state
    setState(prev => ({
      ...prev,
      selectedCode: null,
      selectedContent: null,
      isSidePanelOpen: false
    }));
  }, [isEditing]);

  // Add useEffect to set up and clean up click handler
  useEffect(() => {
    // Store ref in a variable inside the effect
    const container = documentContainerRef.current;

    if (container) {
      container.addEventListener('click', handlePageClick);
    }

    return () => {
      // Use the stored variable in cleanup
      if (container) {
        container.removeEventListener('click', handlePageClick);
      }
    };
  }, [handlePageClick]);

  // Local position setters now provided by hook; no extra effect needed

  const handleParagraphApproval = async (code, isApproving) => {
    try {
      const docRef = doc(db, 'patients', patientId);

      // Base path to this section's approval bookkeeping. MODULE_NUMBER is a
      // numeric section id (e.g. "1"). Firestore's SDK backtick-escapes such
      // digit-leading segments automatically when it builds the update mask,
      // so the dotted-string key form is safe here.
      const sectionPath = `dataModel.6_manageReports.sections.${MODULE_NUMBER}`;

      // Write ONLY this paragraph's key rather than reading the whole document
      // and rewriting the entire approvedParagraphs map inside a
      // read-modify-write transaction.
      //
      // Why: runTransaction uses optimistic concurrency — every commit carries
      // a document-version precondition. When a clinician approves several
      // paragraphs in quick succession (especially on slow 3G/4G links), the
      // queued transactions all contend on the single patient document and most
      // fail with "the stored version does not match the required base version"
      // (failed-precondition). A plain updateDoc on a specific field path has no
      // version precondition, and Firestore merges concurrent writes to
      // distinct field paths server-side, so per-paragraph approvals no longer
      // fight over the document. (Paragraph codes are dot-free identifiers, and
      // the four field paths below are siblings — none is an ancestor of
      // another — so there is no conflicting-path rejection.)
      const updateData = {
        [`${sectionPath}.approvedParagraphs.${code}`]: isApproving
          ? {
              approvedAt: serverTimestamp(),
              approvedBy: auth.currentUser.uid,
              approvedByName: auth.currentUser.displayName,
              content: REPORT_DATA_OUTPUTS[code],
            }
          : deleteField(),
        [`${sectionPath}.lastReviewedAt`]: serverTimestamp(),
        [`${sectionPath}.reviewedBy`]: auth.currentUser.uid,
        [`${sectionPath}.reviewedByName`]: auth.currentUser.displayName,
      };

      // Time the write so we can see (in the console) how quickly approvals
      // actually land in Firestore — updateDoc resolves on SERVER ack, so this
      // is the true round-trip, useful when stress-testing on 3G/Slow 4G. The
      // log is deliberately code-only (no patient content) to keep it PII-free.
      const startedAt = performance.now();
      await updateDoc(docRef, updateData);
      const elapsedMs = Math.round(performance.now() - startedAt);
      console.log(
        `[approval] ${isApproving ? 'APPROVED' : 'UNAPPROVED'} "${code}" persisted to Firestore in ${elapsedMs}ms`
      );

      // Analytics mirror of the approval change that just persisted
      // (code only). Both directions share the review control that
      // wrote it, so both share this one call.
      emitParagraphApproval({
        patientId,
        module: MODULE_PREFIX_CODE,
        docStructure,
        code,
        assessmentType: 'adhd',
        isApproving,
      });

      // Mark section as dirty due to approval change
      setSectionDirty(true);
    } catch (error) {
      console.error("Error updating paragraph approval:", error);
      console.error("Error details:", {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      // Re-throw so the caller can observe the failure. Swallowing the error
      // here made failed approval writes invisible: the paragraph stayed green
      // on screen with no persisted approval behind it — the proven cause of
      // Patient Reports finalising with missing content.
      throw error;
    }
  };

  // Approved paragraphs are now loaded by useApprovalTracking

  const handleMarkAsDone = async () => {
    if (!state.selectedCode) {
      console.log('No code selected, cannot mark as done');
      return;
    }

    console.log('Starting handleMarkAsDone for code:', state.selectedCode);
    const paragraphElement = document.getElementById(state.selectedCode);
    if (paragraphElement) {
      try {
        console.log('Calling handleParagraphApproval...');
        // Track the in-flight write so Save & Exit awaits it before finalising.
        trackApproval(
          handleParagraphApproval(state.selectedCode, true).catch(error => {
            console.error('Error in handleParagraphApproval:', error);
            // Roll back the optimistic update and tell the clinician — a
            // silently failed approval is exactly how incomplete sections
            // used to slip through to the Patient Report.
            removeReviewedCode(state.selectedCode);
            toast.error(
              'A paragraph approval did not save and has been returned to "needs review". Please approve it again.',
              { toastId: 'paragraph-approval-save-error' }
            );
          })
        );

        // Find next paragraph before updating UI state
        const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
        const currentIndex = Array.from(allParagraphs).findIndex(p => p.id === state.selectedCode);
        const nextUnreviewed = Array.from(allParagraphs).find((p, index) =>
          index > currentIndex && !reviewedCodes.has(p.id)
        );

        // Update reviewed state
        addReviewedCode(state.selectedCode);

        if (nextUnreviewed && documentContainerRef.current) {
          const containerRect = documentContainerRef.current.getBoundingClientRect();
          const nextRect = nextUnreviewed.getBoundingClientRect();

          const scrollTop = documentContainerRef.current.scrollTop +
            (nextRect.top - containerRect.top) -
            containerRect.height / 3;

          // Then scroll
          documentContainerRef.current.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });

          setTimeout(() => {
            const nextCode = nextUnreviewed.id;

            // Update state with new paragraph
            setState(prev => ({
              ...prev,
              selectedCode: nextCode,
              isSidePanelOpen: true,
              clinicianInputs: state.modelInputsFirestore[nextCode]["Clinician Notes"],
              clinicianDecisions: state.modelInputsFirestore[nextCode]["Clinician Decisions"],
              // checkedExamples: state.modelInputsFirestore[code]["Checked Examples"],
              patientInputs: state.modelInputsFirestore[nextCode]["Patient Prefill"],
              informantInputs: state.modelInputsFirestore[nextCode]["Informant Prefill"]
            }));

            // Pass the new code directly to position update
            setTimeout(() => safeUpdatePosition(nextCode), 0);
          }, 300);
        } else {
          // This was the last paragraph - unselect it
          setState(prev => ({
            ...prev,
            selectedCode: null,
            isSidePanelOpen: false,
            clinicianInputs: null,
            clinicianDecisions: null,
            // checkedExamples: null,
            patientInputs: null,
            informantInputs: null
          }));
        }
      } catch (error) {
        console.error('Error in handleMarkAsDone:', error);
        console.error("Error details:", {
          code: error.code,
          message: error.message,
          stack: error.stack
        });
      }
    }
  };

  // Update handleSkip with the same scroll logic
  const handleSkip = () => {
    if (!state.selectedCode) return;

    const paragraphElement = document.getElementById(state.selectedCode);
    if (paragraphElement) {
      // Find next paragraph BEFORE updating UI state
      const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
      const currentIndex = Array.from(allParagraphs).findIndex(p => p.id === state.selectedCode);
      const nextUnreviewed = Array.from(allParagraphs).find((p, index) =>
        index > currentIndex && !reviewedCodes.has(p.id)
      );

      if (nextUnreviewed && documentContainerRef.current) {
        const containerRect = documentContainerRef.current.getBoundingClientRect();
        const nextRect = nextUnreviewed.getBoundingClientRect();

        const scrollTop = documentContainerRef.current.scrollTop +
          (nextRect.top - containerRect.top) -
          containerRect.height / 3;

        // Then scroll
        documentContainerRef.current.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });

        setTimeout(() => {
          const nextCode = nextUnreviewed.id;

          // Update state with new paragraph
          setState(prev => ({
            ...prev,
            selectedCode: nextCode,
            isSidePanelOpen: true,
            clinicianInputs: state.modelInputsFirestore[nextCode]["Clinician Notes"],
            clinicianDecisions: state.modelInputsFirestore[nextCode]["Clinician Decisions"],
            patientInputs: state.modelInputsFirestore[nextCode]["Patient Prefill"],
            informantInputs: state.modelInputsFirestore[nextCode]["Informant Prefill"]
          }));

          // Pass the new code directly to position update
          setTimeout(() => safeUpdatePosition(nextCode), 0);
        }, 300);
      } else {
        // This was the last paragraph - unselect it
        setState(prev => ({
          ...prev,
          selectedCode: null,
          isSidePanelOpen: false,
          clinicianInputs: null,
          clinicianDecisions: null,
          patientInputs: null,
          informantInputs: null
        }));
      }
    }
  };

  // Add progress calculation
  const calculateProgress = useCallback(() => {
    if (totalParagraphs === 0) return 0;
    return Math.round((reviewedCodes.size / totalParagraphs) * 100);
  }, [reviewedCodes.size, totalParagraphs]);

  return (
    <Layout activePage="dashboard" scrollable={false}>
      <InitialWarningDialog showInitialWarning={showInitialWarning} setShowInitialWarning={setShowInitialWarning} />
      <div className="h-full flex flex-col overflow-hidden  ">

        {/* // Add overlay component when editing */}
        {isEditing && (
          <div
            className="fixed inset-0 bg-gray-900/20 z-10 transition-opacity duration-"
            style={{
              pointerEvents: 'none' // Allow clicks to pass through to underlying elements
            }}
          />
        )}

        {/* Fixed header section */}
        <div className="flex-none pt-4">
          <DocumentReviewHeader
            title={`Reviewing: ${MODULE_TITLE}`}
            reviewedCodes={reviewedCodes}
            totalParagraphs={totalParagraphs}
            isSaving={isSaving}
            handleExitReview={handleExitReview}
            goToFirstUnreviewed={goToFirstUnreviewed}
            calculateProgress={calculateProgress}
          />
          <WarningBar show={showWarning} />

          {/* Compare with fallback provider – non-prod + org flag only */}
          {isCompareAvailable && (
            <CompareModeToolbar
              isLoading={isCompareLoading}
              hasComparison={hasComparison}
              showClaude={showClaude}
              sideBySide={sideBySide}
              progress={compareProgress}
              onGenerate={triggerComparison}
              onShowGemini={() => setViewMode('gemini')}
              onShowClaude={() => setViewMode('claude')}
              onToggleSideBySide={toggleSideBySide}
            />
          )}
        </div>

        {/* Scrollable document section */}
        <div className="flex-1 px-8 pb-8 min-h-0 relative">
          <div className="document-display-container h-full overflow-hidden bg-slate-800">
            {/* Main flex container for document and side panel */}
            <div className="flex h-full">
              {/* Left side - Document container */}
              <div
                ref={documentContainerRef}
                className={`
                  w-1/2 relative m-4 p-16 border shadow-md bg-white
                  ${isEditing ? 'overflow-hidden' : 'overflow-y-auto'}
                `}
              >
                <div className="space-y-8 text-left">
                  {Object.entries(docStructure).map(([title, paragraphs]) => (
                    <Section
                      key={title}
                      title={title}
                      paragraphs={paragraphs}
                    />
                  ))}
                </div>
                <div className="h-[calc(100vh-32rem)]" aria-hidden="true" />

                {/* Floating Action Panel */}
                <FloatingActionPanel
                  panelRef={panelRef}
                  position={panelPosition}
                  visible={Boolean(state.selectedCode)}
                  isEditing={isEditing}
                  hasBeenModified={hasBeenModified(state.selectedCode)}
                  onStartEdit={handleStartEdit}
                  onSave={handleSaveEdit}
                  onCancel={handleCancel}
                  onReset={handleReset}
                  onApprove={handleMarkAsDone}
                  onSkip={handleSkip}
                />
              </div>

              {sideBySide && hasComparison ? (
                <CompareClaudePanel
                  docStructure={docStructure}
                  claudeOutputs={claudeOutputs}
                  reportOutputs={REPORT_DATA_OUTPUTS}
                />
              ) : (
              <div className={`
                  w-2/5 p-4 flex flex-col
                  ${!state.selectedCode ? 'hidden' : ''}
                `}>
                <div className={`
                    flex-1 text-sm bg-gray-100 rounded-xl overflow-y-auto 
                    border shadow-sm
                    flex flex-col h-full
                  `}>
                  {state.selectedCode && (
                    <div className="flex flex-col h-full p-6 overflow-hidden">
                      <div className="flex-1 flex flex-col min-h-0">
                        <div className="flex-none flex justify-between items-center">
                          <p className="text-xl font-bold text-gray-800 mb-2">AI Inputs</p>
                        </div>
                        <div className="overflow-y-auto">
                          {renderModelInput("Your decisions", state.clinicianDecisions)}
                          {renderModelInput("Your notes", state.clinicianInputs)}
                          {/* {renderModelInput("Your checked examples", state.checkedExamples)} */}
                          {renderModelInput("Patient's report", state.patientInputs)}
                          {renderModelInput("Informant comments", state.informantInputs)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}